import { useMutation } from "@tanstack/react-query";
import { isValid } from "date-fns";
import { Archive, ArchiveRestore } from "lucide-react";
import {
  InfiniteListBase,
  ShowBase,
  useDataProvider,
  useGetMany,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
  useTranslate,
  useUpdate,
} from "ra-core";
import { DeleteButton } from "@/components/admin/delete-button";
import { CanDelete } from "../misc/CanDelete";
import { EditButton } from "@/components/admin/edit-button";
import { ReferenceArrayField } from "@/components/admin/reference-array-field";
import { ReferenceField } from "@/components/admin/reference-field";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";

import { CallsSection } from "../calls";
import { MailSection } from "../mail";
import { CompanyAvatar } from "../companies/CompanyAvatar";
import { NoteCreate } from "../notes/NoteCreate";
import { NotesIterator } from "../notes/NotesIterator";
import { useConfigurationContext } from "../root/ConfigurationContext";
import type { Contact, Deal } from "../types";
import { ContactList } from "./ContactList";
import { formatDealCategories, formatISODateString } from "./dealUtils";
import { formatCurrency } from "../misc/formatCurrency";
import { OneCBlock } from "./OneCBlock";
import {
  FromBitrixBadge,
  NotFromBitrix,
  useReadOnly,
} from "../misc/fromBitrix";

export const DealShow = ({ open, id }: { open: boolean; id?: string }) => {
  const redirect = useRedirect();
  const handleClose = () => {
    redirect("list", "deals");
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="lg:max-w-4xl p-4 overflow-y-auto max-h-9/10 top-1/20 translate-y-0">
        {id ? (
          <ShowBase id={id}>
            <DealShowContent />
          </ShowBase>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const DealShowContent = () => {
  const translate = useTranslate();
  const { dealCategories, currency } = useConfigurationContext();
  const record = useRecordContext<Deal>();
  const readOnly = useReadOnly(record);
  if (!record) return null;

  return (
    <>
      {/* min-w-0: on a phone the card keeps to the dialog's width (no sideways scroll) */}
      <div className="space-y-2 min-w-0">
        {record.archived_at ? <ArchivedTitle /> : null}
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap justify-between items-start gap-2 mb-8">
            {/* without the buttons the close cross sits at the end of this row */}
            <div
              className={`flex flex-wrap items-center gap-x-4 gap-y-2 min-w-0 ${readOnly ? "pr-10" : ""}`}
            >
              <ReferenceField
                source="company_id"
                reference="companies"
                link="show"
              >
                <CompanyAvatar />
              </ReferenceField>
              <h2 className="text-xl font-semibold break-words min-w-0">
                {record.name}
              </h2>
              <FromBitrixBadge record={record} />
            </div>
            {/* a Bitrix24 deal in Bitrix24 mode is changed in Bitrix24 */}
            <NotFromBitrix record={record}>
              <div
                className={`flex gap-2 ${record.archived_at ? "" : "pr-12"}`}
              >
                {record.archived_at ? (
                  <>
                    <UnarchiveButton record={record} />
                    <CanDelete resource="deals">
                      <DeleteButton />
                    </CanDelete>
                  </>
                ) : (
                  <>
                    <ArchiveButton record={record} />
                    <EditButton />
                  </>
                )}
              </div>
            </NotFromBitrix>
          </div>

          <div className="flex flex-wrap gap-x-8 gap-y-3 m-4">
            {/* a deal made of a lead has no expected date yet */}
            {record.expected_closing_date ? (
              <div className="flex flex-col mr-10">
                <span className="text-xs text-muted-foreground tracking-wide">
                  {translate("resources.deals.fields.expected_closing_date")}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-sm">
                    {isValid(new Date(record.expected_closing_date))
                      ? formatISODateString(record.expected_closing_date)
                      : translate("resources.deals.invalid_date")}
                  </span>
                  {new Date(record.expected_closing_date) < new Date() ? (
                    <Badge variant="destructive">
                      {translate("crm.common.past")}
                    </Badge>
                  ) : null}
                </div>
              </div>
            ) : null}

            <div className="flex flex-col mr-10">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.amount")}
              </span>
              <span className="text-sm">
                {formatCurrency(record.amount, currency)}
              </span>
            </div>

            {!!record.categories?.length && (
              <div className="flex flex-col mr-10">
                <span className="text-xs text-muted-foreground tracking-wide">
                  {translate("resources.deals.fields.categories")}
                </span>
                <span className="text-sm">
                  {formatDealCategories(dealCategories, record.categories)}
                </span>
              </div>
            )}

            <div className="flex flex-col mr-10">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.stage")}
              </span>
              <span className="text-sm">{record.stage}</span>
            </div>
          </div>

          {!!record.contact_ids?.length && (
            <div className="m-4">
              <div className="flex flex-col min-h-12 mr-10">
                <span className="text-xs text-muted-foreground tracking-wide">
                  {translate("resources.deals.fields.contact_ids")}
                </span>
                <ReferenceArrayField
                  source="contact_ids"
                  reference="contacts_summary"
                >
                  <ContactList />
                </ReferenceArrayField>
              </div>
            </div>
          )}

          {record.description && (
            <div className="m-4 whitespace-pre-line">
              <span className="text-xs text-muted-foreground tracking-wide">
                {translate("resources.deals.fields.description")}
              </span>
              <p className="text-sm leading-6">{record.description}</p>
            </div>
          )}

          <OneCBlock deal={record} />

          <div className="m-4">
            <Separator className="mb-4" />
            <DealCalls deal={record} />
          </div>

          <div className="m-4">
            <Separator className="mb-4" />
            <DealMail deal={record} />
          </div>

          <div className="m-4">
            <Separator className="mb-4" />
            <InfiniteListBase
              resource="deal_notes"
              filter={{ deal_id: record.id }}
              sort={{ field: "date", order: "DESC" }}
              perPage={25}
              disableSyncWithLocation
              storeKey={false}
              empty={<NoteCreate reference={"deals"} />}
            >
              <NotesIterator reference="deals" />
            </InfiniteListBase>
          </div>
        </div>
      </div>
    </>
  );
};

/** The deal's calls; «Позвонить» dials the first phone of its contacts. */
const DealCalls = ({ deal }: { deal: Deal }) => {
  const { data: contacts } = useGetMany<Contact>(
    "contacts",
    { ids: deal.contact_ids ?? [] },
    { enabled: !!deal.contact_ids?.length },
  );
  const phone = (contacts ?? [])
    .flatMap((c) => c?.phone_jsonb ?? [])
    .map((p) => p?.number)
    .find((n) => !!n?.trim());
  return <CallsSection filter={{ deal_id: deal.id }} phone={phone} />;
};

/** The deal's letters; «Написать письмо» goes to the first e-mail of its contacts. */
const DealMail = ({ deal }: { deal: Deal }) => {
  const { data: contacts } = useGetMany<Contact>(
    "contacts",
    { ids: deal.contact_ids ?? [] },
    { enabled: !!deal.contact_ids?.length },
  );
  const email = (contacts ?? [])
    .flatMap((c) => c?.email_jsonb ?? [])
    .map((e) => e?.email)
    .find((x) => !!x?.trim());
  return <MailSection card={{ dealId: deal.id }} defaultTo={email} />;
};

const ArchivedTitle = () => {
  const translate = useTranslate();
  return (
    <div className="bg-orange-500 px-6 py-4">
      <h3 className="text-lg font-bold text-white">
        {translate("resources.deals.archived.title")}
      </h3>
    </div>
  );
};

const ArchiveButton = ({ record }: { record: Deal }) => {
  const translate = useTranslate();
  const [update] = useUpdate();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();
  const handleClick = () => {
    update(
      "deals",
      {
        id: record.id,
        data: { archived_at: new Date().toISOString() },
        previousData: record,
      },
      {
        onSuccess: () => {
          redirect("list", "deals");
          notify("resources.deals.archived.success", {
            type: "info",
            undoable: false,
          });
          refresh();
        },
        onError: () => {
          notify("resources.deals.archived.error", {
            type: "error",
          });
        },
      },
    );
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      className="flex items-center gap-2 h-9"
    >
      <Archive className="w-4 h-4" />
      {translate("resources.deals.archived.action")}
    </Button>
  );
};

const UnarchiveButton = ({ record }: { record: Deal }) => {
  const translate = useTranslate();
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const notify = useNotify();
  const refresh = useRefresh();

  const { mutate } = useMutation({
    mutationFn: () => dataProvider.unarchiveDeal(record),
    onSuccess: () => {
      redirect("list", "deals");
      notify("resources.deals.unarchived.success", {
        type: "info",
        undoable: false,
      });
      refresh();
    },
    onError: () => {
      notify("resources.deals.unarchived.error", {
        type: "error",
      });
    },
  });

  const handleClick = () => {
    mutate();
  };

  return (
    <Button
      onClick={handleClick}
      size="sm"
      variant="outline"
      className="flex items-center gap-2 h-9"
    >
      <ArchiveRestore className="w-4 h-4" />
      {translate("resources.deals.unarchived.action")}
    </Button>
  );
};
